import { observer } from 'mobx-react';
import { Fragment, useEffect } from 'react';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Card } from '@monorepo/base/src/components/card/card';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { useCampaignInputOptions } from '../../../hooks/apis/use-campaign-input-options';
import { IAdminxStore } from '../../../stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { AdvertiserModel } from '../../../models/advertiser.model';
import { AccountsModel } from '../../../models/account.model';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import styles from './destination.form.module.scss';
import { hasForbiddenCid, requiresCidMacro } from '../../../utils/destination-validation';

interface IDestinationFormProps {
	isEdit: boolean;
}

export const DestinationForm = observer(({ isEdit }: IDestinationFormProps) => {
	useCampaignInputOptions();

	const { destinationStore, campaignStore } = useStores<IAdminxStore>();
	const crudStore = destinationStore.getCrud();
	const formStore = crudStore.getFormStore();
	const destination = crudStore.getData();

	const advertisers = campaignStore.getInputOptions().getAdvertisers();
	const accounts = campaignStore.getInputOptions().getAccounts();
	const advertiser = destination.getAdvertiser();
	const account = destination.getAccount();
	const showPayoutRows = account?.getUseInternalPricing() === false;
	const pricingRows = destination.getDestinationPricing();
	const destUrl = destination.getDestUrl();
	const useInternalPricing = account?.getUseInternalPricing();

	const advertiserNames = advertisers.map(_advertiser => _advertiser.getName() || '');
	const accountNames = accounts.map(_account => _account.getName() || '');

	useEffect(() => {
		if (!showPayoutRows || pricingRows.length > 0) {
			return;
		}
		destination.addPricingRow();
	}, [showPayoutRows]);

	const onAdvertiserSelect = (advertiserName: option) => {
		const selectedAdvertiser = advertisers.find(_advertiser => _advertiser.getName() === advertiserName);
		if (selectedAdvertiser) {
			destination.setAdvertiser(new AdvertiserModel(selectedAdvertiser));
			formStore.clearError('advertiser');
		}
	};

	const onAccountSelect = (accountName: option) => {
		const selectedAccount = accounts.find(_account => _account.getName() === accountName);
		if (selectedAccount) {
			destination.setAccount(new AccountsModel(selectedAccount));
			formStore.clearError('account');
			formStore.clearError('dest_url');

			if (selectedAccount.getUseInternalPricing() === false && destination.getDestinationPricing().length === 0) {
				destination.addPricingRow();
			}
		}
	};

	const nameField = (
		<Input
			value={destination.getName() || ''}
			placeholder="Name"
			required={true}
			onValue={value => {
				destination.setName(value);
				formStore.clearError('name');
			}}
			error={formStore.getErrors().get('name')?.getMsg()}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_name')] }}
		/>
	);

	const productIdField = (
		<Input
			value={destination.getYid() || ''}
			placeholder="Product ID (yid)"
			onValue={value => destination.setYid(value)}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_yid')] }}
		/>
	);

	return (
		<Fragment>
			<Card title="Details" debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_form')] }}>
				{isEdit ? (
					<Fragment>
						{nameField}
						<Spacer y={2} />
						{productIdField}
						<Spacer y={2} />
						<Input
							value={advertiser?.getName() || ''}
							placeholder="Advertiser"
							disabled={true}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_advertiser')] }}
						/>
						<Spacer y={2} />
						<Input
							value={account?.getName() || ''}
							placeholder="Account"
							disabled={true}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_account')] }}
						/>
					</Fragment>
				) : (
					<Fragment>
						{nameField}
						<Spacer y={2} />
						{productIdField}
						<Spacer y={2} />
						<Dropdown
							required={true}
							defaultOption={advertiser?.getName()}
							label="Select an Advertiser"
							options={advertiserNames}
							onSelect={(advertiserName: Set<option> | undefined) =>
								onAdvertiserSelect(advertiserName?.values().next().value)
							}
							error={formStore.getErrors().get('advertiser')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_advertiser')] }}
						/>
						<Dropdown
							required={true}
							defaultOption={account?.getName()}
							label="Select Account"
							options={accountNames}
							onSelect={(accountName: Set<option> | undefined) => onAccountSelect(accountName?.values().next().value)}
							error={formStore.getErrors().get('account')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_account')] }}
						/>
					</Fragment>
				)}

				<Spacer y={2} />

				<Textarea
					wrapperClassName={styles.destUrlWrapper}
					inputWrapperClassName={styles.textareaWrapper}
					value={destination.getDestUrl()}
					placeholder="Destination URL"
					required={true}
					onValue={value => {
						destination.setDestUrl(value);
						formStore.clearError('dest_url');
					}}
					error={formStore.getErrors().get('dest_url')?.getMsg()}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'destination_dest_url')] }}
				/>

				{useInternalPricing === true && !requiresCidMacro(destUrl) ? (
					<Fragment>
						<Spacer y={1} />
						<div className={styles.pricingError}>Destination URL must contain cid=@@CCID@@</div>
					</Fragment>
				) : null}
				{useInternalPricing === false && hasForbiddenCid(destUrl) ? (
					<Fragment>
						<Spacer y={1} />
						<div className={styles.pricingError}>Destination URL must not contain a cid parameter</div>
					</Fragment>
				) : null}
			</Card>

			{showPayoutRows ? (
				<Fragment>
					<Spacer y={2} />
					{formStore.getErrors().get('destination_prices')?.getMsg() ? (
						<div className={styles.pricingError}>{formStore.getErrors().get('destination_prices')?.getMsg()}</div>
					) : null}
					{pricingRows.map((row, index) => (
						<Card key={row.getDevId()} title="Payout" icon="currency-dollar-circle" className={styles.pricingRow}>
							<Icon
								onClick={() => destination.removePricingRow(row.id!)}
								disabled={pricingRows.length <= 1}
								isMFP
								className={styles.closeIcon}>
								x-close
							</Icon>
							<div className={styles.pricingFields}>
								<div className={styles.pricingField}>
									<Input
										className={styles.inputSize}
										value={row.getGeo()}
										placeholder="Geo (e.g. ALL, US)"
										required={true}
										onValue={value => {
											row.setGeo(value.toUpperCase());
											formStore.clearError(`destination_prices_geo_${index}`);
										}}
										error={formStore.getErrors().get(`destination_prices_geo_${index}`)?.getMsg()}
										debugProps={{ dataAttrs: [new DataAttribute('id', `destination_prices_geo_${index}`)] }}
									/>
								</div>
								<div className={styles.pricingField}>
									<Input
										className={styles.inputSize}
										type="positiveNumber"
										value={row.getDefaultPayout()?.toString() || ''}
										placeholder="Default Payout"
										required={true}
										onValue={value => {
											row.setDefaultPayout(parseFloat(value) || 0);
											formStore.clearError(`destination_prices_payout_${index}`);
										}}
										error={formStore.getErrors().get(`destination_prices_payout_${index}`)?.getMsg()}
										debugProps={{ dataAttrs: [new DataAttribute('id', `destination_prices_payout_${index}`)] }}
									/>
								</div>
							</div>
							{pricingRows.length - 1 === index ? (
								<PrimaryButton
									className={styles.addRowBtn}
									onClick={() => destination.addPricingRow()}
									iconSize="20px"
									iconColor="rgb(99, 102, 241)"
									icon="plus">
									Add Payout Row
								</PrimaryButton>
							) : null}
						</Card>
					))}
				</Fragment>
			) : null}
		</Fragment>
	);
});
